/**
 * DENVER_ParkingLotStriping_Landing_v01_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01
 * Date: 2025-08-31 (America/Denver)
 *
 * Based on: EASY_RENTERS_LandingPage_v02O_WIZARD
 * Keeps:
 *  - Wizard/Vertical toggle with progress + blur-driven validation.
 *  - Modular payload builder -> string-only wire payload -> postLead().
 *  - Sectioned confirmation (no JSON dump).
 *
 * Tailored for: Denver Parking Lot Striping lead generation
 * Steps:
 *  1) Contact & Property
 *  2) Project Details
 *  3) Notes & Submit
 *  4) Confirmation
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "services/images/PARKING-LOT/";

/* ============================================================
   TYPES
============================================================ */
type ServiceType =
  | "restripe"
  | "new-layout"
  | "sealcoat"
  | "crack-seal"
  | "pothole-repair"
  | "wheelstops"
  | "stencils"
  | "ada-upgrade";

type SurfaceType = "" | "asphalt" | "concrete" | "mixed";

interface ILeadPayload {
  // Contact
  contactName: string;
  email: string;
  phone: string;
  businessName?: string;
  roleTitle?: string;

  // Property
  lotAddress1: string;
  lotAddress2?: string;
  city: string;
  state: string;
  zipCode: string;
  surfaceType: SurfaceType;

  // Project
  spaceCountApprox?: number;        // Approx # of stalls
  isNightWorkOk?: boolean;
  needsTrafficControl?: boolean;
  targetStartDate?: string;         // date
  budgetRange?: string;             // free text like "Under $5k" etc.
  servicesRequested: ServiceType[]; // multiselect

  // Extras
  photoLinks?: string;              // URLs (comma/space separated)
  notes?: string;

  // Meta
  utm?: { source?: string; medium?: string; campaign?: string };
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const digitsOnly = (s: string) => (s ?? "").replace(/\D+/g, "");
const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
const isValidPhone = (raw: string) => digitsOnly(raw).length === 10;
const isValidZip = (raw: string) => digitsOnly(raw).length === 5;
const formatPhonePretty = (raw: string) => {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const dash = (v?: string | number) =>
  v !== undefined && v !== null && String(v).trim().length ? String(v) : "—";

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "stripe-hero-1.jpg",
    alt: "Freshly striped Denver parking lot at sunset",
    title: "Crisp, Code-Compliant Striping",
    subline: "Fast turnarounds. Clean lines. Zero guesswork.",
    smallCaption: "Property managers trust us citywide.",
  },
  {
    src: imagePath + "sealcoat-hero-2.jpg",
    alt: "Sealcoated asphalt with clear stall lines",
    title: "Sealcoat • Crack Seal • Stencils",
    subline: "Extend pavement life and improve curb appeal.",
    smallCaption: "Protect your investment.",
  },
  {
    src: imagePath + "ada-upgrade-hero-3.jpg",
    alt: "ADA accessible parking with blue stencil and signage",
    title: "ADA Layouts & Upgrades",
    subline: "Accessible stalls, aisles, and signage done right.",
    smallCaption: "Stay compliant and avoid fines.",
  },
];

/* ============================================================
   MAIN
============================================================ */
export const DENVER_ParkingLotStriping_Landing_v01_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const steps = ["Contact & Property", "Project Details", "Notes & Submit", "Confirmation"] as const;
  const [currentStep, setCurrentStep] = useState(0);

  // Contact
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");

  // Property
  const [lotAddress1, setLotAddress1] = useState("");
  const [lotAddress2, setLotAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("CO"); // Denver default
  const [zipCode, setZipCode] = useState("");
  const [surfaceType, setSurfaceType] = useState<SurfaceType>("asphalt");

  // Project
  const [spaceCountApprox, setSpaceCountApprox] = useState<number | undefined>(undefined);
  const [servicesRequested, setServicesRequested] = useState<ServiceType[]>(["restripe"]);
  const [targetStartDate, setTargetStartDate] = useState<string>("");
  const [budgetRange, setBudgetRange] = useState<string>("");
  const [isNightWorkOk, setIsNightWorkOk] = useState<boolean>(true);
  const [needsTrafficControl, setNeedsTrafficControl] = useState<boolean>(false);

  // Extras
  const [photoLinks, setPhotoLinks] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Blur-driven validation controls
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators
  const V = {
    contactName: contactName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    lotAddress1: lotAddress1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZip(zipCode),

    surfaceType: surfaceType !== "",

    // project
    servicesRequested: servicesRequested.length > 0,
    spaceCountApprox:
      spaceCountApprox === undefined ||
      (Number.isFinite(spaceCountApprox) && (spaceCountApprox as number) >= 0 && (spaceCountApprox as number) <= 2000),

    // optional
    targetStartDate: !targetStartDate || /^\d{4}-\d{2}-\d{2}$/.test(targetStartDate),
    budgetRange: !budgetRange || budgetRange.trim().length >= 2,
    photoLinks: !photoLinks || photoLinks.trim().length >= 5,
    notes: !notes || notes.trim().length >= 2,
    businessName: !businessName || businessName.trim().length >= 2,
    roleTitle: !roleTitle || roleTitle.trim().length >= 2,
  };

  const valid = useMemo(() => {
    return (
      V.contactName &&
      V.email &&
      V.phone &&
      V.lotAddress1 &&
      V.city &&
      V.state &&
      V.zip &&
      V.surfaceType &&
      V.servicesRequested &&
      V.spaceCountApprox &&
      V.targetStartDate &&
      V.budgetRange &&
      V.photoLinks &&
      V.notes &&
      V.businessName &&
      V.roleTitle
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.contactName && V.email && V.phone && V.lotAddress1 && V.city && V.state && V.zip && V.surfaceType;
      case 1:
        return V.servicesRequested && V.spaceCountApprox && V.targetStartDate && V.budgetRange;
      case 2:
        return V.photoLinks && V.notes && V.businessName && V.roleTitle;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) =>
      setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["contactName", "email", "phone", "lotAddress1", "city", "state", "zip", "surfaceType"]);
    else if (currentStep === 1) touchMany(["servicesRequested", "spaceCountApprox", "targetStartDate", "budgetRange"]);
    else if (currentStep === 2) touchMany(["photoLinks", "notes", "businessName", "roleTitle"]);
    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT (MODULARIZED)
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    return {
      contactName: contactName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),
      businessName: businessName?.trim() || "",
      roleTitle: roleTitle?.trim() || "",

      lotAddress1: lotAddress1.trim(),
      lotAddress2: lotAddress2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),
      surfaceType,

      spaceCountApprox:
        typeof spaceCountApprox === "number" && Number.isFinite(spaceCountApprox) ? spaceCountApprox : undefined,
      isNightWorkOk,
      needsTrafficControl,
      targetStartDate: targetStartDate || "",
      budgetRange: budgetRange?.trim() || "",
      servicesRequested,

      photoLinks: photoLinks?.trim() || "",
      notes: notes?.trim() || "",

      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.parking-lot-striping.lead",
      itemClass: "lead.request",
      itemType: "denver.parkinglot.striping.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
      else if (typeof v === "boolean") out[k] = v ? "true" : "false";
      else if (Array.isArray(v)) out[k] = v.join(",");
      else if (v && typeof v === "object") {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else out[k] = "";
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch everything for vertical submit
    setTouched((t) => ({
      ...t,
      contactName: true, email: true, phone: true,
      lotAddress1: true, city: true, state: true, zip: true, surfaceType: true,
      servicesRequested: true, spaceCountApprox: true, targetStartDate: true, budgetRange: true,
      photoLinks: true, notes: true, businessName: true, roleTitle: true,
    }));

    if (!valid) {
      setSubmitError("Please complete required fields (highlighted).");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] wire payload:", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Denver <span className="text-primary">Parking Lot</span> Striping
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Talk to a project manager:</div>
              <Button variant="outline-primary" href="tel:3033374433">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.60), rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">ADA Upgrades</Badge>
                  <Badge bg="light" text="dark">Fast Turnarounds</Badge>
                  <Badge bg="light" text="dark">Night Work Available</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={scrollToForm}>Get Free Estimate</Button>
                </div>
              </div>
            </Col>

            {/* FORM CARD */}
            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Request a Free Estimate</div>
                      <small className="text-body-secondary">Tell us about your lot—get a fast, accurate quote.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM START */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT & PROPERTY */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact &amp; Property</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={12}>
                                    <Form.Label htmlFor="contactName">Contact Name</Form.Label>
                                    <Form.Control
                                      id="contactName"
                                      value={contactName}
                                      onChange={(e) => setContactName(e.target.value)}
                                      placeholder="Jane Doe"
                                      required
                                      {...vp("contactName", V.contactName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control
                                      id="email"
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="you@example.com"
                                      required
                                      {...vp("email", V.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control
                                      id="phone"
                                      inputMode="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="(303) 555-0123"
                                      required
                                      {...vp("phone", V.phone)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="businessName">Business / Property Name (optional)</Form.Label>
                                    <Form.Control
                                      id="businessName"
                                      value={businessName}
                                      onChange={(e) => setBusinessName(e.target.value)}
                                      placeholder="Shopping Center, HOA, etc."
                                      {...vpOpt("businessName", !!businessName.trim())}
                                    />
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="roleTitle">Your Role (optional)</Form.Label>
                                    <Form.Control
                                      id="roleTitle"
                                      value={roleTitle}
                                      onChange={(e) => setRoleTitle(e.target.value)}
                                      placeholder="Owner, Property Manager, GC, etc."
                                      {...vpOpt("roleTitle", !!roleTitle.trim())}
                                    />
                                  </Col>

                                  <Col md={8}>
                                    <Form.Label htmlFor="lotAddress1">Lot Address</Form.Label>
                                    <Form.Control
                                      id="lotAddress1"
                                      value={lotAddress1}
                                      onChange={(e) => setLotAddress1(e.target.value)}
                                      placeholder="1234 Example St"
                                      required
                                      {...vp("lotAddress1", V.lotAddress1)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter the job address.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="lotAddress2">Suite / Area (optional)</Form.Label>
                                    <Form.Control
                                      id="lotAddress2"
                                      value={lotAddress2}
                                      onChange={(e) => setLotAddress2(e.target.value)}
                                      placeholder="North lot, Bldg B..."
                                      {...vpOpt("lotAddress2", !!lotAddress2.trim())}
                                    />
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="city">City</Form.Label>
                                    <Form.Control
                                      id="city"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      placeholder="Denver"
                                      required
                                      {...vp("city", V.city)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter city.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="state">State</Form.Label>
                                    <Form.Select
                                      id="state"
                                      value={state}
                                      onChange={(e) => setState(e.target.value)}
                                      required
                                      {...vp("state", V.state)}
                                    >
                                      <option value="">Select</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="zipCode">Zip</Form.Label>
                                    <Form.Control
                                      id="zipCode"
                                      inputMode="numeric"
                                      value={zipCode}
                                      onChange={(e) => setZipCode(e.target.value)}
                                      placeholder="80202"
                                      required
                                      {...vp("zip", V.zip)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter 5-digit zip.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="surfaceType">Surface Type</Form.Label>
                                    <Form.Select
                                      id="surfaceType"
                                      value={surfaceType}
                                      onChange={(e) => setSurfaceType(e.target.value as SurfaceType)}
                                      required
                                      {...vp("surfaceType", V.surfaceType)}
                                    >
                                      <option value="">Select</option>
                                      <option value="asphalt">Asphalt</option>
                                      <option value="concrete">Concrete</option>
                                      <option value="mixed">Mixed / Unsure</option>
                                    </Form.Select>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: PROJECT DETAILS */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Project Details</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="services">Services Needed</Form.Label>
                                    <Form.Select
                                      id="services"
                                      multiple
                                      value={servicesRequested}
                                      onChange={(e) => {
                                        const options = Array.from(e.currentTarget.selectedOptions).map((o) => o.value as ServiceType);
                                        setServicesRequested(options);
                                      }}
                                      required
                                      {...vp("servicesRequested", V.servicesRequested)}
                                    >
                                      <option value="restripe">Restripe Existing Lot</option>
                                      <option value="new-layout">New Layout / New Lot</option>
                                      <option value="sealcoat">Sealcoat</option>
                                      <option value="crack-seal">Crack Seal</option>
                                      <option value="pothole-repair">Pothole Repair</option>
                                      <option value="wheelstops">Wheel Stops</option>
                                      <option value="stencils">Stencils / Legends</option>
                                      <option value="ada-upgrade">ADA Upgrade (stalls/aisles)</option>
                                    </Form.Select>
                                    <div className="form-text">Hold Ctrl/Cmd to select multiple.</div>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="spaces">Approx. # of Stalls</Form.Label>
                                    <Form.Control
                                      id="spaces"
                                      inputMode="numeric"
                                      value={spaceCountApprox ?? ""}
                                      onChange={(e) => {
                                        const v = e.target.value.trim();
                                        setSpaceCountApprox(v === "" ? undefined : Number(v));
                                      }}
                                      placeholder="e.g., 120"
                                      {...vp("spaceCountApprox", V.spaceCountApprox)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–2000 allowed (or leave blank).</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="target">Target Start Date (optional)</Form.Label>
                                    <Form.Control
                                      id="target"
                                      type="date"
                                      value={targetStartDate}
                                      onChange={(e) => setTargetStartDate(e.target.value)}
                                      {...vpOpt("targetStartDate", !!targetStartDate)}
                                    />
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="budget">Budget Range (optional)</Form.Label>
                                    <Form.Control
                                      id="budget"
                                      value={budgetRange}
                                      onChange={(e) => setBudgetRange(e.target.value)}
                                      placeholder="Under $5k, $5k–$15k, etc."
                                      {...vpOpt("budgetRange", !!budgetRange)}
                                    />
                                  </Col>

                                  <Col md={6} className="pt-md-4">
                                    <Form.Check
                                      id="night"
                                      type="checkbox"
                                      label="Night / Off-hours work is OK"
                                      checked={isNightWorkOk}
                                      onChange={(e) => setIsNightWorkOk(e.currentTarget.checked)}
                                    />
                                  </Col>

                                  <Col md={6} className="pt-md-4">
                                    <Form.Check
                                      id="traffic"
                                      type="checkbox"
                                      label="Traffic control needed"
                                      checked={needsTrafficControl}
                                      onChange={(e) => setNeedsTrafficControl(e.currentTarget.checked)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: NOTES & SUBMIT */}
                          {currentStep === 2 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Notes &amp; Photos</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={12}>
                                    <Form.Label htmlFor="photos">Photo Links (optional)</Form.Label>
                                    <Form.Control
                                      id="photos"
                                      value={photoLinks}
                                      onChange={(e) => setPhotoLinks(e.target.value)}
                                      placeholder="Paste public URLs (separate by comma/space)"
                                      {...vpOpt("photoLinks", !!photoLinks.trim())}
                                    />
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
                                    <Form.Control
                                      id="notes"
                                      as="textarea"
                                      rows={3}
                                      value={notes}
                                      onChange={(e) => setNotes(e.target.value)}
                                      placeholder="Timing constraints, phasing, repaint colors, stencil counts, etc."
                                      {...vpOpt("notes", !!notes.trim())}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls (no buttons on final step) */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" /> Submitting...
                                  </>
                                ) : (
                                  <>Get My Free Estimate</>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              {/* Contact & Property */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact &amp; Property</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label htmlFor="contactNameV">Contact Name</Form.Label>
                                      <Form.Control
                                        id="contactNameV"
                                        value={contactName}
                                        onChange={(e) => setContactName(e.target.value)}
                                        placeholder="Jane Doe"
                                        required
                                        {...vp("contactName", V.contactName)}
                                      />
                                      <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control
                                        id="emailV"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        {...vp("email", V.email)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control
                                        id="phoneV"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="(303) 555-0123"
                                        required
                                        {...vp("phone", V.phone)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="businessNameV">Business / Property Name (optional)</Form.Label>
                                      <Form.Control
                                        id="businessNameV"
                                        value={businessName}
                                        onChange={(e) => setBusinessName(e.target.value)}
                                        placeholder="Shopping Center, HOA, etc."
                                        {...vpOpt("businessName", !!businessName.trim())}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="roleTitleV">Your Role (optional)</Form.Label>
                                      <Form.Control
                                        id="roleTitleV"
                                        value={roleTitle}
                                        onChange={(e) => setRoleTitle(e.target.value)}
                                        placeholder="Owner, Property Manager, GC, etc."
                                        {...vpOpt("roleTitle", !!roleTitle.trim())}
                                      />
                                    </Col>

                                    <Col md={8}>
                                      <Form.Label htmlFor="lotAddress1V">Lot Address</Form.Label>
                                      <Form.Control
                                        id="lotAddress1V"
                                        value={lotAddress1}
                                        onChange={(e) => setLotAddress1(e.target.value)}
                                        placeholder="1234 Example St"
                                        required
                                        {...vp("lotAddress1", V.lotAddress1)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter the job address.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="lotAddress2V">Suite / Area (optional)</Form.Label>
                                      <Form.Control
                                        id="lotAddress2V"
                                        value={lotAddress2}
                                        onChange={(e) => setLotAddress2(e.target.value)}
                                        placeholder="North lot, Bldg B..."
                                        {...vpOpt("lotAddress2", !!lotAddress2.trim())}
                                      />
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="cityV">City</Form.Label>
                                      <Form.Control
                                        id="cityV"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        placeholder="Denver"
                                        required
                                        {...vp("city", V.city)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter city.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">State</Form.Label>
                                      <Form.Select
                                        id="stateV"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                        {...vp("state", V.state)}
                                      >
                                        <option value="">Select</option>
                                        {US_STATES.map((s) => (
                                          <option key={s.code} value={s.code}>{s.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="zipCodeV">Zip</Form.Label>
                                      <Form.Control
                                        id="zipCodeV"
                                        inputMode="numeric"
                                        value={zipCode}
                                        onChange={(e) => setZipCode(e.target.value)}
                                        placeholder="80202"
                                        required
                                        {...vp("zip", V.zip)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter 5-digit zip.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="surfaceTypeV">Surface Type</Form.Label>
                                      <Form.Select
                                        id="surfaceTypeV"
                                        value={surfaceType}
                                        onChange={(e) => setSurfaceType(e.target.value as SurfaceType)}
                                        required
                                        {...vp("surfaceType", V.surfaceType)}
                                      >
                                        <option value="">Select</option>
                                        <option value="asphalt">Asphalt</option>
                                        <option value="concrete">Concrete</option>
                                        <option value="mixed">Mixed / Unsure</option>
                                      </Form.Select>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Project Details */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Project Details</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="servicesV">Services Needed</Form.Label>
                                      <Form.Select
                                        id="servicesV"
                                        multiple
                                        value={servicesRequested}
                                        onChange={(e) => {
                                          const options = Array.from(e.currentTarget.selectedOptions).map((o) => o.value as ServiceType);
                                          setServicesRequested(options);
                                        }}
                                        required
                                        {...vp("servicesRequested", V.servicesRequested)}
                                      >
                                        <option value="restripe">Restripe Existing Lot</option>
                                        <option value="new-layout">New Layout / New Lot</option>
                                        <option value="sealcoat">Sealcoat</option>
                                        <option value="crack-seal">Crack Seal</option>
                                        <option value="pothole-repair">Pothole Repair</option>
                                        <option value="wheelstops">Wheel Stops</option>
                                        <option value="stencils">Stencils / Legends</option>
                                        <option value="ada-upgrade">ADA Upgrade (stalls/aisles)</option>
                                      </Form.Select>
                                      <div className="form-text">Hold Ctrl/Cmd to select multiple.</div>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="spacesV">Approx. # of Stalls</Form.Label>
                                      <Form.Control
                                        id="spacesV"
                                        inputMode="numeric"
                                        value={spaceCountApprox ?? ""}
                                        onChange={(e) => {
                                          const v = e.target.value.trim();
                                          setSpaceCountApprox(v === "" ? undefined : Number(v));
                                        }}
                                        placeholder="e.g., 120"
                                        {...vp("spaceCountApprox", V.spaceCountApprox)}
                                      />
                                      <Form.Control.Feedback type="invalid">0–2000 allowed (or leave blank).</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="targetV">Target Start Date (optional)</Form.Label>
                                      <Form.Control
                                        id="targetV"
                                        type="date"
                                        value={targetStartDate}
                                        onChange={(e) => setTargetStartDate(e.target.value)}
                                        {...vpOpt("targetStartDate", !!targetStartDate)}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="budgetV">Budget Range (optional)</Form.Label>
                                      <Form.Control
                                        id="budgetV"
                                        value={budgetRange}
                                        onChange={(e) => setBudgetRange(e.target.value)}
                                        placeholder="Under $5k, $5k–$15k, etc."
                                        {...vpOpt("budgetRange", !!budgetRange)}
                                      />
                                    </Col>

                                    <Col md={6} className="pt-md-4">
                                      <Form.Check
                                        id="nightV"
                                        type="checkbox"
                                        label="Night / Off-hours work is OK"
                                        checked={isNightWorkOk}
                                        onChange={(e) => setIsNightWorkOk(e.currentTarget.checked)}
                                      />
                                    </Col>

                                    <Col md={6} className="pt-md-4">
                                      <Form.Check
                                        id="trafficV"
                                        type="checkbox"
                                        label="Traffic control needed"
                                        checked={needsTrafficControl}
                                        onChange={(e) => setNeedsTrafficControl(e.currentTarget.checked)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Notes & Photos */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Notes &amp; Photos</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label htmlFor="photosV">Photo Links (optional)</Form.Label>
                                      <Form.Control
                                        id="photosV"
                                        value={photoLinks}
                                        onChange={(e) => setPhotoLinks(e.target.value)}
                                        placeholder="Paste public URLs (separate by comma/space)"
                                        {...vpOpt("photoLinks", !!photoLinks.trim())}
                                      />
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="notesV">Notes (optional)</Form.Label>
                                      <Form.Control
                                        id="notesV"
                                        as="textarea"
                                        rows={3}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Timing constraints, phasing, repaint colors, stencil counts, etc."
                                        {...vpOpt("notes", !!notes.trim())}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Free Estimate</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about striping services. No spam. Opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                  {/* FORM END */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Simple, contractor-friendly process.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your lot</h6>
                  <p className="mb-0 text-body-secondary">Address, surface type, and rough stall count.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Pick services</h6>
                  <p className="mb-0 text-body-secondary">Restripe, new layout, ADA, sealcoat, stencils, and more.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">Get a fast estimate</h6>
                  <p className="mb-0 text-body-secondary">We schedule work around your peak hours.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Denver Parking Lot Striping. All rights reserved.
              <div className="mt-2">
                Services subject to site conditions and scope. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033374433">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ------------------------------------------------------------
   Confirmation block (Sectioned, readable, no JSON)
------------------------------------------------------------ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

//   const svcList = (app.servicesRequested || []).map((s) => s.replaceAll("-", " ")).join(", ");

const svcList = (app.servicesRequested || [])
  .map((s) => (s as string).replace(/-/g, " "))
  .join(", ");


  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {/* Status */}
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A project manager will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        {/* Sections */}
        <div className="mt-3">
          <Section title="Contact">
            <KV label="Name"  value={dash(app.contactName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
            <KV label="Business / Property" value={dash(app.businessName)} />
            <KV label="Role" value={dash(app.roleTitle)} />
          </Section>

          <Section title="Property">
            <KV label="Address" value={`${dash(app.lotAddress1)} ${app.lotAddress2 ? "• " + app.lotAddress2 : ""}`} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
            <KV label="Surface Type" value={dash(app.surfaceType)} />
          </Section>

          <Section title="Project">
            <KV label="Services" value={svcList || "—"} />
            <KV label="Approx. Stalls" value={dash(app.spaceCountApprox ?? "")} />
            <KV label="Target Start Date" value={dash(app.targetStartDate)} />
            <KV label="Budget Range" value={dash(app.budgetRange)} />
            <KV label="Night Work OK" value={app.isNightWorkOk ? "Yes" : "No"} />
            <KV label="Traffic Control" value={app.needsTrafficControl ? "Required" : "Not needed"} />
          </Section>

          <Section title="Notes & Photos">
            <KV label="Photo Links" value={dash(app.photoLinks)} />
            <Col className="mb-0">
              <dt className="small text-muted mb-1">Notes</dt>
              <dd className="mb-0">
                <div className="p-3 bg-body-tertiary border rounded">{dash(app.notes)}</div>
              </dd>
            </Col>
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033374433" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">
      {children}
    </Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default DENVER_ParkingLotStriping_Landing_v01_WIZARD;


